import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload } from 'lucide-react';
import { apiService } from '../services/api';
import { Pipeline, PipelineStatus } from '../types/pipeline';
import { useNotificationStore, useAuthStore } from '../store';
import { formatApiError } from '../utils/errorFormatter';
import { PipelineTable } from '../components/PipelineList/PipelineTable';
import { PipelineFilters } from '../components/PipelineList/PipelineFilters';
import { EmptyState } from '../components/PipelineList/EmptyState';

export const PipelineList: React.FC = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [filteredPipelines, setFilteredPipelines] = useState<Pipeline[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PipelineStatus | 'ALL'>('ALL');
  const [showDisabled, setShowDisabled] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<'SELF' | 'ALL'>('ALL');
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'status' | 'workflow_count'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const loadPipelines = useCallback(async () => {
    try {
      setIsLoading(true);

      // Build API params
      const params: { status?: string; created_by?: string } = {};

      // Apply owner filter if set to SELF
      if (ownerFilter === 'SELF' && user) {
        params.created_by = user.username;
      }

      const data = await apiService.getPipelines(params);
      setPipelines(data);
    } catch (error: any) {
      console.error('Error loading pipelines:', error);
      addNotification(
        `Failed to load pipelines: ${formatApiError(error)}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  }, [addNotification, ownerFilter, user]);

  const applyFilters = useCallback(() => {
    let filtered = [...pipelines];

    // Exclude DISABLED pipelines by default (unless showDisabled is true or explicitly filtered)
    if (!showDisabled && statusFilter !== 'DISABLED') {
      filtered = filtered.filter(p => p.status !== 'DISABLED');
    }

    // Apply status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'workflow_count':
          comparison = a.workflow_count - b.workflow_count;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredPipelines(filtered);
  }, [pipelines, searchQuery, statusFilter, showDisabled, sortBy, sortOrder]);

  // Fetch pipelines on mount
  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  // Apply filters whenever dependencies change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleCreateNew = () => {
    navigate('/pipelines/builder');
  };

  const handleEdit = (pipeline: Pipeline) => {
    navigate(`/pipelines/builder/${pipeline.id}`);
  };

  const handleDuplicate = async (pipeline: Pipeline) => {
    try {
      const duplicated = await apiService.duplicatePipeline(pipeline.id);
      addNotification(`Pipeline duplicated: Created "${duplicated.name}"`, 'success');
      loadPipelines(); // Refresh list
    } catch (error: any) {
      console.error('Error duplicating pipeline:', error);
      addNotification(
        `Failed to duplicate pipeline: ${formatApiError(error)}`,
        'error'
      );
    }
  };

  const handleDelete = async (pipeline: Pipeline) => {
    try {
      await apiService.deletePipeline(pipeline.id);
      addNotification(`Pipeline deleted: "${pipeline.name}" has been disabled`, 'success');
      loadPipelines(); // Refresh list
    } catch (error: any) {
      console.error('Error deleting pipeline:', error);
      addNotification(
        `Failed to delete pipeline: ${formatApiError(error)}`,
        'error'
      );
    }
  };

  const handleSort = (column: 'name' | 'created_at' | 'status' | 'workflow_count') => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleOwnerFilterChange = (filter: 'SELF' | 'ALL') => {
    setOwnerFilter(filter);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('ALL');
    setShowDisabled(false);
    setOwnerFilter('SELF');
  };

  const handleExport = async (pipeline: Pipeline) => {
    try {
      const blob = await apiService.exportPipeline(pipeline.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Sanitize filename
      const sanitizedName = pipeline.name.replace(/[^a-z0-9-_]/gi, '-').toLowerCase();
      a.download = `pipeline-${pipeline.id}-${sanitizedName}.json`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      addNotification(`Pipeline "${pipeline.name}" exported successfully`, 'success');
    } catch (error: any) {
      console.error('Error exporting pipeline:', error);
      addNotification(
        `Failed to export pipeline: ${formatApiError(error)}`,
        'error'
      );
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      addNotification('Please select a valid JSON file', 'error');
      return;
    }

    try {
      setIsImporting(true);
      const imported = await apiService.importPipeline(file);
      addNotification(`Pipeline "${imported.name}" imported successfully`, 'success');
      loadPipelines(); // Refresh list
    } catch (error: any) {
      console.error('Error importing pipeline:', error);
      
      // Handle detailed validation errors
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (typeof detail === 'object' && detail.message) {
          // Structured error with validation details
          let errorMessage = detail.message;
          
          if (detail.missing_processors) {
            errorMessage += `\n\nMissing processors:\n${detail.missing_processors.join(', ')}`;
          }
          
          if (detail.validation_errors) {
            const errors = detail.validation_errors.map((err: any) => 
              `- ${err.message}${err.step_id ? ` (${err.step_id})` : ''}`
            ).join('\n');
            errorMessage += `\n\nValidation errors:\n${errors}`;
          }
          
          addNotification(errorMessage, 'error');
        } else {
          // Simple error message
          addNotification(`Failed to import pipeline: ${detail}`, 'error');
        }
      } else {
        addNotification(
          `Failed to import pipeline: ${formatApiError(error)}`,
          'error'
        );
      }
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Empty state conditions
  const hasNoPipelines = pipelines.length === 0;
  const hasNoResults = !hasNoPipelines && filteredPipelines.length === 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Pipelines
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage workflow pipeline configurations
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleImportClick}
                disabled={isImporting}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="import-pipeline-button"
              >
                <Upload className="w-5 h-5" />
                {isImporting ? 'Importing...' : 'Import Pipeline'}
              </button>
              <button
                onClick={handleCreateNew}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                data-testid="create-pipeline-button"
              >
                <Plus className="w-5 h-5" />
                Create New Pipeline
              </button>
              
              {/* Hidden file input for import */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportFile}
                className="hidden"
                data-testid="import-file-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {isLoading ? (
          <div className="text-center py-12" data-testid="loading-state">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading pipelines...</p>
          </div>
        ) : hasNoPipelines ? (
          <EmptyState
            type="no-pipelines"
            onCreateFirst={handleCreateNew}
          />
        ) : (
          <>
            <PipelineFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusFilterChange={(status) => {
                setStatusFilter(status);
                // Auto-enable showDisabled if DISABLED is explicitly selected
                if (status === 'DISABLED') {
                  setShowDisabled(true);
                }
              }}
              showDisabled={showDisabled}
              onShowDisabledChange={setShowDisabled}
              ownerFilter={ownerFilter}
              onOwnerFilterChange={handleOwnerFilterChange}
              onClearFilters={handleClearFilters}
            />

            {hasNoResults ? (
              <EmptyState type="no-results" />
            ) : (
              <PipelineTable
                pipelines={filteredPipelines}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onExport={handleExport}
                onDelete={handleDelete}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

